"use client";

import { memo, MouseEvent, useEffect, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import toast from "react-hot-toast";

import IconFavoriteActive from "@/icons/ui/feedback/heart-filled.svg";
import IconFavorite from "@/icons/ui/feedback/heart.svg";
import { useFavorites } from "@/providers/FavoriteContext";
import { useSession } from "@/providers/SessionProvider";
import { useShowFavoriteID } from "@/services/hooks/useShowFavorite";
import { userService } from "@/services/user.service";
import { cn } from "@/utils/cn";

interface Props {
  id: string;
  className?: string;
  label?: React.ReactNode;
}

export const AddToFavorite = memo(({ id, className, label }: Props) => {
  const t = useTranslations();
  const { favorites, toggleFavorite, isInitialized } = useFavorites();
  const { isAuthenticated } = useSession();
  const [localActive, setLocalActive] = useState<boolean>(false);

  const { refetchFavoriteID } = useShowFavoriteID(isAuthenticated);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isInitialized) {
      setLocalActive(favorites.has(id));
    }
  }, [isInitialized, favorites, id]);

  const { mutate } = useMutation({
    mutationKey: ["addToFavoriteProperty"],
    mutationFn: (id: string) =>
      userService.addFavoriteProperty({ propertyId: [id] }),
    onError(error: any) {
      if (error?.status === 401) {
        return toast.error(t("Incorrect login or password!"));
      }
      if (isAuthenticated) {
        refetchFavoriteID();
      }
      toast.error(t("Something went wrong, please try again later!"));
    },
    onSuccess: async () => {
      toggleFavorite(id);
      if (isAuthenticated) {
        refetchFavoriteID();
      }
      queryClient.invalidateQueries({ queryKey: ["favoriteCounts"] });
      toast.success(t("Successfully added to favorite"));
    },
  });

  const removeFavoriteProperty = useMutation({
    mutationKey: ["removeFavoriteProperty"],
    mutationFn: (id: string) =>
      userService.removeFavoriteProperty({ propertyId: [id] }),
    onError(error: any) {
      if (error?.status === 401) {
        return toast.error(t("Incorrect login or password!"));
      }
      if (isAuthenticated) {
        refetchFavoriteID();
      }
      toast.error(t("Something went wrong, please try again later!"));
    },
    onSuccess: async () => {
      toggleFavorite(id);
      if (isAuthenticated) {
        refetchFavoriteID();
      }
      queryClient.invalidateQueries({ queryKey: ["favoriteCounts"] });
      toast.success(t("The object has been successfully deleted!"));
    },
  });

  const addToFavorite = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!id) {
      return;
    }

    if (isAuthenticated) {
      if (favorites.has(id)) {
        removeFavoriteProperty.mutate(id);
      } else {
        mutate(id);
      }
    } else {
      toggleFavorite(id);
      const newState = !favorites.has(id);
      setLocalActive(newState);
      toast.success(
        newState
          ? t("Successfully added to favorite")
          : t("The object has been successfully deleted!"),
      );
    }
  };

  return (
    <button
      className={cn(
        "cursor-pointer flex items-center",
        label ? "gap-2" : "w-8 h-auto",
        className,
      )}
      type={"button"}
      aria-label={
        localActive ? t("Remove from favorites") : t("Add to favorites")
      }
      onClick={addToFavorite}
    >
      {localActive ? (
        <IconFavoriteActive
          className={cn("text-red", label ? "w-8 h-8" : "w-full h-auto")}
        />
      ) : (
        <IconFavorite className={cn(label ? "w-8 h-8" : "w-full h-auto")} />
      )}
      {label || null}
    </button>
  );
});

AddToFavorite.displayName = "AddToFavorite";
